// src/components/Dashboard/charts/FoldersByUserChart.tsx
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Empty } from "antd";

interface UserData {
  collaborator__id: string;
  collaborator__email: string;
  collaborator__first_name: string;
  collaborator__last_name: string;
  count: number;
  completed: number;
}

interface FoldersByCollaboratorChartProps {
  data: UserData[];
}

const FoldersByCollaboratorChart: React.FC<FoldersByCollaboratorChartProps> = ({
  data,
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Empty description="No data available" />
      </div>
    );
  }

  // Format the data for the chart
  const formattedData = data
    .map((item) => {
      const name =
        `${item.collaborator__first_name} ${item.collaborator__last_name}`.trim() ||
        item.collaborator__email.split("@")[0];

      // Calculate the number of in-progress folders
      const inProgress = item.count - item.completed;

      // Calculate completion percentage
      const completionPercentage = Math.round(
        (item.completed / item.count) * 100,
      );

      return {
        name,
        total: item.count,
        completed: item.completed,
        inProgress,
        completionPercentage,
        labelAnchor: 0,
        // Store the original data for tooltip
        originalData: item,
      };
    })
    .sort((a, b) => b.total - a.total) // Sort by total count
    .slice(0, 10); // Limit to top 10 users for better visibility

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="font-medium text-gray-600">{label}</p>
          <p className="text-green-600">
            Completados: <span className="font-bold">{data.completed}</span>{" "}
            folders
          </p>
          <p className="text-orange-500">
            En Progreso: <span className="font-bold">{data.inProgress}</span>{" "}
            folders
          </p>
          <p className="text-blue-600">
            Totales: <span className="font-bold">{data.total}</span> folders
          </p>
          <p className="text-purple-600 font-medium mt-1">
            Porcentaje Completados:{" "}
            <span className="font-bold">{data.completionPercentage}%</span>
          </p>
          <p className="text-gray-500 text-xs mt-1">
            {data.originalData.collaborator__email}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label component to show completion percentage
  const renderCustomizedLabel = (props: any) => {
    const { x, y, width, value } = props;
    return (
      <text
        x={x + width / 2}
        y={y - 10}
        fill="#333"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={12}
        fontWeight="bold"
      >
        {`${value}%`}
      </text>
    );
  };

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer>
        <BarChart
          data={formattedData}
          margin={{
            top: 20, // Added more top margin for percentage labels
            right: 30,
            left: 20,
            bottom: 5,
          }}
          barGap={0}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis
            dataKey="name"
            tick={{ fill: "#6b7280", fontSize: 12 }}
            tickLine={{ stroke: "#e5e7eb" }}
            axisLine={{ stroke: "#e5e7eb" }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis
            tick={{ fill: "#6b7280" }}
            tickLine={{ stroke: "#e5e7eb" }}
            axisLine={{ stroke: "#e5e7eb" }}
          />
          <Tooltip content={<CustomTooltip />} />
          {/*<Legend />*/}

          {/* Completed folders bar */}
          <Bar
            dataKey="completed"
            name="Completed Folders"
            stackId="a"
            fill="#10b981" // Green color for completed
            radius={[0, 0, 0, 0]}
          />

          {/* In progress folders bar */}
          <Bar
            dataKey="inProgress"
            name="In Progress Folders"
            stackId="a"
            fill="#f97316" // Orange color for in progress
            radius={[4, 4, 0, 0]}
          />

          {/* Transparent zero-value bar that anchors the percentage label on
              top of the stack. Recharts 3 skips labels of zero-height bars,
              so users with no folders in progress lost their label. */}
          <Bar
            dataKey="labelAnchor"
            stackId="a"
            fill="transparent"
            minPointSize={1}
            legendType="none"
          >
            <LabelList
              dataKey="completionPercentage"
              position="top"
              content={renderCustomizedLabel}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default FoldersByCollaboratorChart;
